/**
 * Reine MA-Auswertungs-Pipeline (ohne React).
 * Quelle der Wahrheit für ExecutiveSummaryModal-Zahlen via normalizeMonthsForMA
 * und für Betty Live Context (1:1 Parität).
 */

import { isLegacyLockedMetrics } from '../store/slices/coreDataSlice';
import type { MonatsDaten } from '../store/types';
import { resolveEmployeeEffectiveWageBasis } from './personnelCostAdapter';

const COUNT_HJ = 6;

export type AmpelLabel = 'Rentabel' | 'Kostendeckend' | 'Unter Kostendeckung';
export type AmpelCategory = 'top' | 'ok' | 'warn' | 'crit';

/** Identisch zu ExecutiveSummaryModal.NormalizedMonthData */
export type NormalizedMonthData = {
  monatName: string;
  index: number;
  bedienumsatzBrutto: number;
  vkUmsatzBrutto: number;
  gesamtBrutto: number;
  gesamtNetto: number;
  mindestNetto: number;
  kostendeckungNetto: number;
  bonusBedienung: number;
  bonusVK: number;
  isLocked: boolean;
  activeLohnfaktor: number;
  urlaub: number;
  krank: number;
};

export type LeistungCalcContext = {
  employee: any;
  leistung: any;
  lohnfaktorData: any;
  globalSettings: any;
  countrySettings: any;
  leistungsprovision: any;
  kalkulatorState: any;
  isHJ1GesperrtGlobal: boolean;
  isHJ2Freigegeben: boolean;
  isTeamBonusMode: boolean;
  mwstFaktor: number;
  lohnfaktorAktiv: number;
  geraeteListe: any[];
  baseMaterialProzent: number;
  gemeinkostenPercent: number;
  avgSozialabgabenPercent: number;
  lohnfortzahlungPercent: number;
  verlustPercent: number;
  storeSettings: any;
};

const ensureArr = (val: any, fallback: any[] = []): any[] => {
  if (Array.isArray(val)) return val;
  return fallback;
};

const safeDiv = (numerator: number, denominator: number): number => {
  const num = Number(numerator);
  const den = Number(denominator);
  if (!den || isNaN(num) || isNaN(den)) return 0;
  return num / den;
};

export const buildEmployeeBasisFromFrozen = (frozen: any, employee: any) => {
  if (!frozen || !employee) return employee;
  return {
    ...employee,
    name: frozen.name ?? employee.name,
    wochenstunden: frozen.wochenstunden ?? employee.wochenstunden,
    arbeitstage: frozen.arbeitstage ?? employee.arbeitstage,
    grundgehalt: frozen.grundgehalt ?? employee.grundgehalt,
    lohnzusatz: frozen.lohnzusatz ?? employee.lohnzusatz,
    lohnkostenzusatz: frozen.lohnzusatz ?? employee.lohnkostenzusatz ?? employee.lohnzusatz,
    bonus: frozen.bonus ?? employee.bonus,
    taetigkeit: frozen.taetigkeit ?? employee.taetigkeit,
    zugehoerigkeit: frozen.zugehoerigkeit ?? employee.zugehoerigkeit,
    country: frozen.country ?? employee.country,
    kategorie: frozen.kategorie ?? employee.kategorie,
    grossWage: frozen.grossWage ?? employee.grossWage,
    netWage: frozen.netWage ?? employee.netWage,
    costoAziendaleOverride: frozen.costoAziendaleOverride ?? employee.costoAziendaleOverride,
    arbeitetMitGeraeten: frozen.arbeitetMitGeraeten ?? employee.arbeitetMitGeraeten ?? employee.hasGeraete,
    hasGeraete: frozen.arbeitetMitGeraeten ?? employee.arbeitetMitGeraeten ?? employee.hasGeraete,
    _frozenLohnfaktor: frozen.lohnfaktorAktiv,
    _frozenKostendeckung: frozen.kostendeckungsfaktor1,
    _frozenDeviceIds: Array.isArray(frozen.selectedDeviceIds) ? frozen.selectedDeviceIds : undefined,
  };
};

export const getHj1ProvisionFaktoren = (frozen: any, safeProvision: any) => {
  const prov = frozen?.provisionErstesHalbjahr ?? safeProvision?.erstesHalbjahr;
  return {
    bedienung: (prov?.provisionDienstleistung ?? 10) / 100,
    vk: (prov?.provisionVerkauf ?? 10) / 100,
  };
};

/**
 * Ampel analog ExecutiveSummaryModal currentStatusKategorie (Zeilen 322–327).
 */
export function resolveAmpelCategory(
  gesamtNetto: number,
  mindestNetto: number,
  kostendeckungNetto: number,
  gesamtBrutto: number
): AmpelCategory {
  if (gesamtNetto >= mindestNetto) return 'top';
  if (gesamtNetto >= kostendeckungNetto) return 'ok';
  if (gesamtBrutto > 0) return 'warn';
  return 'crit';
}

export function resolveAmpelLabel(
  gesamtNetto: number,
  mindestNetto: number,
  kostendeckungNetto: number,
  gesamtBrutto: number
): AmpelLabel {
  const cat = resolveAmpelCategory(gesamtNetto, mindestNetto, kostendeckungNetto, gesamtBrutto);
  if (cat === 'top') return 'Rentabel';
  if (cat === 'ok') return 'Kostendeckend';
  return 'Unter Kostendeckung';
}

/**
 * Exakt die Mapping-Logik aus LeistungPage.normalizedMonthsForMA.
 */
export function normalizeMonthsForMA(
  h1Details: any[],
  h2Details: any[],
  rawH1: any[],
  rawH2: any[],
  mwstFaktor: number
): NormalizedMonthData[] {
  const list: NormalizedMonthData[] = [];
  const safeH1 = ensureArr(rawH1);
  const safeH2 = ensureArr(rawH2);
  const d1 = ensureArr(h1Details);
  const d2 = ensureArr(h2Details);

  safeH1.forEach((m: any, idx: number) => {
    const calc = d1[idx] || {};
    list.push({
      monatName: m?.monat || `Monat ${idx + 1}`,
      index: idx,
      bedienumsatzBrutto: m?.bedienUmsatz || 0,
      vkUmsatzBrutto: m?.vkUmsatz || 0,
      gesamtBrutto: calc.gesamtUmsatzBrutto || 0,
      gesamtNetto: safeDiv(calc.gesamtUmsatzBrutto || 0, mwstFaktor),
      mindestNetto: safeDiv(calc.angepassterSollUmsatz || 0, mwstFaktor),
      kostendeckungNetto: safeDiv(calc._activeBasisMetrics?.kostendeckungsumsatz || 0, mwstFaktor),
      bonusBedienung: calc.bonusBedienung || 0,
      bonusVK: calc.bonusVK || 0,
      isLocked: m?.festmachen || false,
      activeLohnfaktor: safeDiv(
        calc._activeBasisMetrics?.sollUmsatzMonat,
        calc._activeBasisMetrics?.effektiverBruttolohn
      ),
      urlaub: Number(m?.urlaubsTage) || 0,
      krank: Number(m?.krankheitsTage) || 0,
    });
  });

  safeH2.forEach((m: any, idx: number) => {
    const calc = d2[idx] || {};
    list.push({
      monatName: m?.monat || `Monat ${idx + 7}`,
      index: idx + 6,
      bedienumsatzBrutto: m?.bedienUmsatz || 0,
      vkUmsatzBrutto: m?.vkUmsatz || 0,
      gesamtBrutto: calc.gesamtUmsatzBrutto || 0,
      gesamtNetto: safeDiv(calc.gesamtUmsatzBrutto || 0, mwstFaktor),
      mindestNetto: safeDiv(calc.angepassterSollUmsatz || 0, mwstFaktor),
      kostendeckungNetto: safeDiv(calc._activeBasisMetrics?.kostendeckungsumsatz || 0, mwstFaktor),
      bonusBedienung: calc.bonusBedienung || 0,
      bonusVK: calc.bonusVK || 0,
      isLocked: m?.festmachen || false,
      activeLohnfaktor: safeDiv(
        calc._activeBasisMetrics?.sollUmsatzMonat,
        calc._activeBasisMetrics?.effektiverBruttolohn
      ),
      urlaub: Number(m?.urlaubsTage) || 0,
      krank: Number(m?.krankheitsTage) || 0,
    });
  });

  return list;
}

/**
 * Volle MA-Halbjahres-Pipeline (Inhalt von useLeistungCalculations).
 */
export function computeEmployeeLeistung(ctx: LeistungCalcContext) {
  const {
    employee,
    leistung,
    lohnfaktorData,
    globalSettings,
    countrySettings,
    leistungsprovision,
    kalkulatorState,
    isHJ1GesperrtGlobal,
    isHJ2Freigegeben,
    isTeamBonusMode,
    mwstFaktor,
    lohnfaktorAktiv,
    geraeteListe,
    baseMaterialProzent,
    gemeinkostenPercent,
    avgSozialabgabenPercent,
    lohnfortzahlungPercent,
    verlustPercent,
    storeSettings,
  } = ctx;

  if (!employee || !leistung || !lohnfaktorData || !globalSettings || !countrySettings) return null;

  const lockedMetricsIndex = leistung.lockedMetrics;
  const legacySnapshot = isLegacyLockedMetrics(lockedMetricsIndex) ? lockedMetricsIndex.metrics : null;
  const safeProvision = leistungsprovision || {};

  const mandantOverrideOhneMat =
    Boolean(kalkulatorState?.useEchterStundensatz) && Number(kalkulatorState?.echterStundensatz) > 0
      ? Number(kalkulatorState.echterStundensatz)
      : 0;
  const adminSollOhneMat =
    Number(kalkulatorState?.barometerStundenfaktor) > 0
      ? Number(kalkulatorState.barometerStundenfaktor)
      : 0;
  const activeSalonStundensatz = mandantOverrideOhneMat > 0 ? mandantOverrideOhneMat : adminSollOhneMat;

  const calculateBasisMetrics = (basisdaten: any, halbjahrKey: 'halbjahr1' | 'halbjahr2' = 'halbjahr1') => {
    const metrics: any = { ...basisdaten };
    metrics.wochenstundenBerechnet =
      basisdaten.arbeitstage * safeDiv(basisdaten.wochenstunden, basisdaten.arbeitstage);
    metrics.monatsStunden = basisdaten.wochenstunden * globalSettings.Wochensatz;
    metrics.tagesArbeitszeit = safeDiv(basisdaten.wochenstunden, basisdaten.arbeitstage);

    const effectiveBase = resolveEmployeeEffectiveWageBasis(basisdaten, {
      selectedCountryId: storeSettings?.selectedCountryId,
      countrySettings,
    });
    const lohnzusatzVal = Number(basisdaten.lohnkostenzusatz || basisdaten.lohnzusatz || 0);
    metrics.grundgehalt = Math.max(0, effectiveBase - lohnzusatzVal);
    metrics.effektiverBruttolohn = effectiveBase;
    metrics.gesamtlohnBrutto = metrics.effektiverBruttolohn + (Number(basisdaten.bonus) || 0);

    metrics.stdLohnGrund = safeDiv(metrics.grundgehalt, metrics.monatsStunden);
    metrics.stdLohnEffektiv = safeDiv(metrics.effektiverBruttolohn, metrics.monatsStunden);
    metrics.stdLohnGesamt = safeDiv(metrics.gesamtlohnBrutto, metrics.monatsStunden);

    let currentLohnfaktor =
      typeof basisdaten._frozenLohnfaktor === 'number' && basisdaten._frozenLohnfaktor > 0
        ? basisdaten._frozenLohnfaktor
        : lohnfaktorAktiv;

    const safeDeviceIds = ensureArr(
      Array.isArray(basisdaten._frozenDeviceIds) ? basisdaten._frozenDeviceIds : leistung.selectedDeviceIds
    );
    if (basisdaten.arbeitetMitGeraeten && safeDeviceIds.length > 0) {
      const alleGeraete = ensureArr(geraeteListe);
      let totalGeraeteProzent = 0;
      safeDeviceIds.forEach((devId: string) => {
        const geraet = alleGeraete.find((g: any) => g && g.id === devId);
        if (geraet) {
          totalGeraeteProzent += geraet.kostenProzent || 0;
        }
      });
      const faktorZuschlag = (totalGeraeteProzent / 100) * 10;
      currentLohnfaktor += faktorZuschlag;
    }

    metrics.sollUmsatzMonat = metrics.effektiverBruttolohn * currentLohnfaktor;
    metrics.sollUmsatzProStunde = safeDiv(metrics.sollUmsatzMonat, metrics.monatsStunden);
    metrics.sollUmsatzProTagBerechnet = metrics.sollUmsatzProStunde * metrics.tagesArbeitszeit;
    const auslastungVorgabe = 75;
    metrics.auslastungVorgabe = auslastungVorgabe;
    metrics.requiredRate75 = safeDiv(metrics.sollUmsatzProStunde, auslastungVorgabe / 100);

    metrics.salonStundensatz = activeSalonStundensatz;

    const liveKostendeckungsfaktor =
      halbjahrKey === 'halbjahr2'
        ? Number(lohnfaktorData.kostendeckungsfaktor2) ||
          Number(lohnfaktorData.kostendeckungsfaktor1) ||
          2.8
        : Number(lohnfaktorData.kostendeckungsfaktor1) || 2.8;
    metrics.kostendeckungsfaktor =
      typeof basisdaten._frozenKostendeckung === 'number' && basisdaten._frozenKostendeckung > 0
        ? basisdaten._frozenKostendeckung
        : liveKostendeckungsfaktor;
    metrics.kostendeckungsumsatz = metrics.effektiverBruttolohn * metrics.kostendeckungsfaktor;

    const prozentWerte = {
      mwstProzent: countrySettings.MwSt_Satz || 19.0,
      materialProzent: baseMaterialProzent,
      gemeinkostenProzent: gemeinkostenPercent,
      sozialabgabenProzent: avgSozialabgabenPercent,
      lohnfortzahlungProzent: lohnfortzahlungPercent,
      verlustProzent: storeSettings?.global?.Verlust_1 || verlustPercent,
      liquiditaetProzent: storeSettings?.global?.Liquiditaet || 10.0,
    };
    metrics.prozentWerte = prozentWerte;
    const kosten = {
      mwst: metrics.effektiverBruttolohn * (prozentWerte.mwstProzent / 100),
      material: metrics.effektiverBruttolohn * (prozentWerte.materialProzent / 100),
      gemeinkosten: metrics.effektiverBruttolohn * (prozentWerte.gemeinkostenProzent / 100),
      sozialabgaben: metrics.effektiverBruttolohn * (prozentWerte.sozialabgabenProzent / 100),
      lohnfortzahlung: metrics.effektiverBruttolohn * (prozentWerte.lohnfortzahlungProzent / 100),
      verlust: metrics.effektiverBruttolohn * (prozentWerte.verlustProzent / 100),
      liquiditaet: metrics.effektiverBruttolohn * (prozentWerte.liquiditaetProzent / 100),
    };
    metrics.analyseKosten = kosten;
    metrics.totpunktMonatsumsatz =
      metrics.effektiverBruttolohn + Object.values(kosten).reduce((a: number, b: number) => a + b, 0);
    metrics.prognoseGewinnMonat = metrics.sollUmsatzMonat - metrics.totpunktMonatsumsatz;
    return metrics;
  };

  const processHalbjahr = (
    halbjahrKey: 'halbjahr1' | 'halbjahr2',
    halbjahrData: MonatsDaten[],
    liveBasisMetrics: any,
    liveBonusFaktoren: { bedienung: number; vk: number }
  ) => {
    const legacyBasisMetrics = legacySnapshot
      ? halbjahrKey === 'halbjahr1'
        ? legacySnapshot.metricsHJ1
        : legacySnapshot.metricsHJ2
      : undefined;
    const legacyBonusFaktoren = legacySnapshot
      ? halbjahrKey === 'halbjahr1'
        ? legacySnapshot.faktorenHJ1
        : legacySnapshot.faktorenHJ2
      : undefined;
    const monthLockedIndex =
      !isLegacyLockedMetrics(lockedMetricsIndex) && lockedMetricsIndex
        ? lockedMetricsIndex[halbjahrKey]
        : undefined;

    const result: {
      monatsDetails: any[];
      summary: any;
      durchschnitt: any;
      realDurchschnitt: any;
    } = { monatsDetails: [], summary: {}, durchschnitt: {}, realDurchschnitt: {} };
    let summeIstUmsatz = 0;
    let anzahlMonateMitUmsatz = 0;

    halbjahrData.forEach((m) => {
      const safeM = m || { bedienUmsatz: 0, vkUmsatz: 0 };
      const bedien = safeM.bedienUmsatz || 0;
      const vk = safeM.vkUmsatz || 0;
      if (bedien + vk > 0) {
        summeIstUmsatz += bedien + vk;
        anzahlMonateMitUmsatz++;
      }
    });
    const avgIstUmsatzMonat = anzahlMonateMitUmsatz > 0 ? summeIstUmsatz / anzahlMonateMitUmsatz : 0;

    result.monatsDetails = halbjahrData.map((monat, index) => {
      const safeMonat =
        monat || {
          bedienUmsatz: 0,
          vkUmsatz: 0,
          urlaubsTage: 0,
          krankheitsTage: 0,
          unproduktiveStunden: 0,
          ueberstundenAuszahlen: false,
          festmachen: false,
        };

      const isLocked = safeMonat.festmachen;
      const monthSnapshot = monthLockedIndex?.[String(index)];
      let basisMetrics = liveBasisMetrics;
      let bonusFaktoren = liveBonusFaktoren;

      if (isLocked) {
        if (monthSnapshot?.basisMetrics) {
          basisMetrics = monthSnapshot.basisMetrics;
          bonusFaktoren = monthSnapshot.bonusFaktoren || liveBonusFaktoren;
        } else if (legacyBasisMetrics) {
          basisMetrics = legacyBasisMetrics;
          bonusFaktoren = legacyBonusFaktoren || liveBonusFaktoren;
        }
      }

      const avgIstUmsatzProStunde = safeDiv(avgIstUmsatzMonat, basisMetrics.monatsStunden);

      const detail: any = {
        _activeBasisMetrics: basisMetrics,
        _activeBonusFaktoren: bonusFaktoren,
      };

      const bedien = safeMonat.bedienUmsatz || 0;
      const vk = safeMonat.vkUmsatz || 0;

      const hasUmsatz = bedien + vk > 0;
      const effectiveUrlaubsTage = hasUmsatz ? safeMonat.urlaubsTage || 0 : 0;

      const unprodStunden = Number(safeMonat.unproduktiveStunden) || 0;
      detail.umsatzUnproduktiv = unprodStunden * basisMetrics.sollUmsatzProStunde;

      let angepassterSollUmsatz = basisMetrics.sollUmsatzMonat;

      if (!safeMonat.ueberstundenAuszahlen) {
        angepassterSollUmsatz -= detail.umsatzUnproduktiv;
      }
      detail.angepassterSollUmsatz = angepassterSollUmsatz;

      detail.vkFaktor = safeDiv(vk, bedien);
      detail.urlaubsAusgleich =
        avgIstUmsatzProStunde * effectiveUrlaubsTage * basisMetrics.tagesArbeitszeit;
      detail.gesamtUmsatzBrutto = bedien + vk + detail.urlaubsAusgleich;
      detail.umsatzProStunde = safeDiv(detail.gesamtUmsatzBrutto, basisMetrics.monatsStunden);

      const umsatzAuslastung =
        (basisMetrics.auslastungVorgabe / 100) *
        safeDiv(detail.umsatzProStunde, basisMetrics.sollUmsatzProStunde) *
        100;
      detail.umsatzauslastung = umsatzAuslastung;

      const deltaBrutto = detail.gesamtUmsatzBrutto - detail.angepassterSollUmsatz;
      detail.deltaNetto = deltaBrutto / mwstFaktor;
      const vkUmsatzNetto = vk / mwstFaktor;

      if (hasUmsatz) {
        if (isTeamBonusMode) {
          detail.bonusBedienung = 0;
          detail.bonusVK = 0;
        } else {
          detail.bonusBedienung = detail.deltaNetto * bonusFaktoren.bedienung;
          detail.bonusVK = deltaBrutto > 0 ? vkUmsatzNetto * bonusFaktoren.vk : 0;
        }
      } else {
        detail.deltaNetto = 0;
        detail.bonusBedienung = 0;
        detail.bonusVK = 0;
      }
      detail.bonusGesamt = detail.bonusBedienung + detail.bonusVK;
      detail.realUmsatzGesamt = bedien + vk;
      return detail;
    });

    const summary = {
      bedienUmsatz: 0,
      vkUmsatz: 0,
      urlaubsTage: 0,
      krankheitsTage: 0,
      gesamtUmsatzBrutto: 0,
      deltaNetto: 0,
      bonusBedienung: 0,
      bonusVK: 0,
      bonusGesamt: 0,
      kostendeckungsumsatz: 0,
      sollUmsatzMonat: 0,
      realUmsatzGesamt: 0,
    };
    let totalVkFaktor = 0;
    let totalUmsatzProStunde = 0;
    let totalUmsatzauslastung = 0;
    let summedKostendeckung = 0;

    halbjahrData.forEach((monat, index) => {
      const safeMonat = monat || {};
      const detail = result.monatsDetails[index] || {};
      const activeMetrics = detail._activeBasisMetrics || liveBasisMetrics;

      const bedien = safeMonat.bedienUmsatz || 0;
      const vk = safeMonat.vkUmsatz || 0;

      const hasUmsatz = bedien + vk > 0;
      summary.bedienUmsatz += bedien;
      summary.vkUmsatz += vk;
      summary.urlaubsTage += Number(safeMonat.urlaubsTage) || 0;
      summary.krankheitsTage += Number(safeMonat.krankheitsTage) || 0;
      summary.gesamtUmsatzBrutto += detail.gesamtUmsatzBrutto || 0;
      summary.deltaNetto += detail.deltaNetto || 0;
      summary.bonusBedienung += detail.bonusBedienung || 0;
      summary.bonusVK += detail.bonusVK || 0;
      summary.bonusGesamt += detail.bonusGesamt || 0;

      summedKostendeckung += activeMetrics.kostendeckungsumsatz || 0;

      summary.sollUmsatzMonat += detail.angepassterSollUmsatz || 0;
      summary.realUmsatzGesamt += detail.realUmsatzGesamt || 0;
      if (hasUmsatz) {
        totalVkFaktor += detail.vkFaktor || 0;
        totalUmsatzProStunde += detail.umsatzProStunde || 0;
      }
      totalUmsatzauslastung += detail.umsatzauslastung || 0;
    });

    summary.kostendeckungsumsatz = summedKostendeckung / COUNT_HJ;
    result.summary = summary;

    const monateMitUmsatz =
      halbjahrData.filter((m) => {
        const safeM = m || {};
        return (safeM.bedienUmsatz || 0) + (safeM.vkUmsatz || 0) > 0;
      }).length || 1;

    result.durchschnitt = {
      bedienUmsatz: safeDiv(summary.bedienUmsatz, monateMitUmsatz),
      vkUmsatz: safeDiv(summary.vkUmsatz, monateMitUmsatz),
      gesamtUmsatzBrutto: safeDiv(summary.gesamtUmsatzBrutto, monateMitUmsatz),
      deltaNetto: safeDiv(summary.deltaNetto, monateMitUmsatz),
      kostendeckungsumsatz: summary.kostendeckungsumsatz,
      sollUmsatzMonat: safeDiv(summary.sollUmsatzMonat, COUNT_HJ),
      vkFaktor: safeDiv(totalVkFaktor, monateMitUmsatz),
      umsatzProStunde: safeDiv(totalUmsatzProStunde, monateMitUmsatz),
      umsatzauslastung: safeDiv(totalUmsatzauslastung, monateMitUmsatz),
    };
    result.realDurchschnitt = {
      bedien: result.durchschnitt.bedienUmsatz,
      vk: result.durchschnitt.vkUmsatz,
      gesamt: result.durchschnitt.bedienUmsatz + result.durchschnitt.vkUmsatz,
    };
    return result;
  };

  const frozenHJ1 =
    isHJ1GesperrtGlobal && isHJ2Freigegeben ? leistung.frozenStammdatenHJ1 : undefined;
  const hj1BasisEmployee = buildEmployeeBasisFromFrozen(frozenHJ1, employee);
  const metricsHJ1 = calculateBasisMetrics(hj1BasisEmployee, 'halbjahr1');
  const faktorenHJ1 = getHj1ProvisionFaktoren(frozenHJ1, safeProvision);
  const safeHj1 = ensureArr(leistung.halbjahr1);
  const processedHJ1 = processHalbjahr('halbjahr1', safeHj1, metricsHJ1, faktorenHJ1);

  const metricsHJ2 = calculateBasisMetrics(employee, 'halbjahr2');
  const faktorenHJ2 = {
    bedienung: (safeProvision.zweitesHalbjahr?.provisionDienstleistung ?? 10) / 100,
    vk: (safeProvision.zweitesHalbjahr?.provisionVerkauf ?? 10) / 100,
  };
  const safeHj2 = ensureArr(leistung.halbjahr2);
  const processedHJ2 = processHalbjahr('halbjahr2', safeHj2, metricsHJ2, faktorenHJ2);

  const metricsJahr = { ...metricsHJ1 };
  const employeeHJ1 = hj1BasisEmployee;

  return {
    metricsHJ1,
    processedHJ1,
    metricsHJ2,
    processedHJ2,
    employee,
    employeeHJ1,
    metricsJahr,
    faktorenHJ1,
    faktorenHJ2,
    leistung,
    isTeamBonusMode,
  };
}

/** Pipeline + normalizeMonthsForMA in einem Schritt. */
export function buildNormalizedMonthsForEmployee(ctx: LeistungCalcContext): NormalizedMonthData[] {
  const calc = computeEmployeeLeistung(ctx);
  if (!calc) return [];
  return normalizeMonthsForMA(
    calc.processedHJ1?.monatsDetails || [],
    calc.processedHJ2?.monatsDetails || [],
    ensureArr(ctx.leistung?.halbjahr1),
    ensureArr(ctx.leistung?.halbjahr2),
    ctx.mwstFaktor
  );
}

export type MonthSnapshotLine = {
  index: number;
  label: string;
  ziel: number;
  ergebnis: number;
  differenz: number;
  ampel: AmpelLabel;
};

export function monthToSnapshotLine(
  m: NormalizedMonthData,
  shortLabel: string
): MonthSnapshotLine {
  const differenz = m.gesamtNetto - m.mindestNetto;
  return {
    index: m.index,
    label: shortLabel,
    ziel: m.mindestNetto,
    ergebnis: m.gesamtNetto,
    differenz,
    ampel: resolveAmpelLabel(
      m.gesamtNetto,
      m.mindestNetto,
      m.kostendeckungNetto,
      m.gesamtBrutto
    ),
  };
}

/** Halbjahr-Aggregation analog Modal (aktive Monate: Umsatz > 0 oder locked). */
export function aggregateHalbjahrSnapshot(
  months: NormalizedMonthData[]
): { ziel: number; ergebnis: number; differenz: number; ampel: AmpelLabel } {
  const active = months.filter((m) => m.gesamtBrutto > 0 || m.isLocked);
  const count = active.length || 1;
  let totalZiel = 0;
  let totalErgebnis = 0;
  let totalKosten = 0;
  let totalBrutto = 0;
  active.forEach((m) => {
    totalZiel += m.mindestNetto;
    totalErgebnis += m.gesamtNetto;
    totalKosten += m.kostendeckungNetto;
    totalBrutto += m.gesamtBrutto;
  });
  const avgZiel = totalZiel / count;
  const avgErgebnis = totalErgebnis / count;
  const avgKosten = totalKosten / count;
  const avgBrutto = totalBrutto / count;
  return {
    ziel: avgZiel,
    ergebnis: avgErgebnis,
    differenz: avgErgebnis - avgZiel,
    ampel: resolveAmpelLabel(avgErgebnis, avgZiel, avgKosten, avgBrutto),
  };
}
